import React, { useState } from 'react';
import { Box, Grid, Table, TableCell, TableFooter, TableHead, TablePagination, TableRow } from '@material-ui/core'
import { Button, Checkbox } from "@paro.io/base-ui";
import EditableTableBody from '../EditableTableBody';
import { IconPlus } from '@paro.io/base-icons';
import * as Sentry from '@sentry/browser'
import axios from 'axios'
import AlertBox from '../AlertBox';

const transformQuickBooksClients = (allClients) => {
    return allClients.map((client) => {
        const billing = client.BillAddr || {};

        return {
            customer: {
                email: client?.email ? client?.email : client.PrimaryEmailAddr?.Address || '',
                firstName: client?.firstName ? client?.firstName : client.GivenName || '',
                lastName: client?.lastName ? client?.lastName : client.FamilyName || '',
                phone: client.PrimaryPhone?.FreeFormNumber || '',
                company: client?.company ? client?.company : client.CompanyName || client.DisplayName || '',
            },
            billing_address: {
                line_1: billing.Line1 || '',
                line_2: billing.Line2 || '',
                city: billing.City || '',
                county: billing.CountrySubDivisionCode || '',
                postcode: billing.PostalCode || '',
                country: billing.Country || 'US',
            },
        };
    });
}

const Step2 = ({
    importedClients,
    setImportedClients,
    setAllClients,
    setUpdateSteps,
    setFetchingQuickbooks,
    setCurrentStep,
    allClients,
    addClientsManually,
    showExistingUsersWarning,
    setShowExistingUsersWarning,
    importedClientsCount,
    setImportedClientsCount,
    setAddNewClientModal,
}) => {

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [errorInClients, setErrorInClients] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [importingClients, setImportingClients] = useState<boolean>(false);
    const [errorInSelectedClients, setErrorInSelectedClients] = useState<boolean>(false);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowCheckbox = (idx: number) => {
        setSelectedRows(prev =>
            prev.includes(idx)
                ? prev.filter(i => i !== idx)
                : [...prev, idx]
        );
    };
    // Select all rows in current page
    const handleHeaderCheckbox = () => {
        const start = page * rowsPerPage;
        const end = Math.min(start + rowsPerPage, importedClients.length);
        const pageIndices = Array.from({ length: end - start }, (_, i) => start + i);
        const allSelected = pageIndices.every(idx => selectedRows.includes(idx));
        if (allSelected) {
            setSelectedRows(prev => prev.filter(idx => !pageIndices.includes(idx)));
        } else {
            setSelectedRows(prev => Array.from(new Set([...prev, ...pageIndices])));
        }
    };
    // Select all rows in table
    const handleSelectAllTable = () => {
        if (selectedRows.length === importedClients.length) {
            setSelectedRows([]);
        } else {
            setSelectedRows(Array.from({ length: importedClients.length }, (_, i) => i));
        }
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleImportClients = async () => {

        setImportingClients(true);
        setFetchingQuickbooks(true);

        // Only import selected clients
        const selectedClients = selectedRows.length > 0
            ? selectedRows.map(idx => importedClients[idx])
            : [];

        const clientsPayload = transformQuickBooksClients(selectedClients);
        const userIdStr = localStorage.getItem('userId');

        const payload = {
            input: {
                clients: clientsPayload,
                userId: Number(userIdStr),
                isByob: true,
            },
        };
        try {
            const res = await axios.post(`${process.env.REACT_APP_V2_ENDPOINT}/createClientFromImports`, payload)

            if (res.status === 200) {
                // Check for existing clients in response
                const resultArr = res?.data ?? [];
                const stillExistingEmails = resultArr?.filter((c: any) => c.status === 'existing')?.map((c: any) => c.email) || [];

                if (stillExistingEmails.length === 0) {
                    setImportedClients([]);
                    setAllClients([]);
                    setUpdateSteps(prev =>
                        prev.map(obj =>
                            obj.number === 3 ? { ...obj, completed: true } : obj
                        ));
                    setCurrentStep(3);
                } else {
                    // There are existing clients
                    const filterUnimportedClients = selectedClients?.filter(client => stillExistingEmails.includes(client.email));

                    setImportedClients(filterUnimportedClients);
                    setAllClients(filterUnimportedClients);
                    setShowExistingUsersWarning(true);

                    const importedCount = resultArr.length - stillExistingEmails.length;
                    if (importedCount > 0) {
                        setImportedClientsCount(`${importedCount}/${resultArr.length}`);
                    }
                }
            }

        } catch (err) {
            console.error("Failed to import clients", err)
            const context = {
                variables: JSON.stringify(payload),
                path: `${process.env.REACT_APP_V2_ENDPOINT}/createClientFromImports`,
            }
            Sentry.captureException(`Error importing clients: ${err}`, {
                contexts: { context },
            })
        } finally {
            setFetchingQuickbooks(false);
            setImportingClients(false);
        }

    }

    const isSelectedAllClients = selectedRows.length === importedClients.length && importedClients.length > 0;

    return (
        <Box sx={{ flex: 1, padding: 50 }}>
            <Grid item xs={12}>
                <Grid container spacing={2} direction="column" style={{ paddingBottom: 6 }}>
                    <h1 className="text-lg font-semibold my-2">Securely Import Your Book of Business</h1>
                    <p>We've securely pulled your client information from the source you connected on the previous screen.</p>
                    {!errorInClients && !showExistingUsersWarning && importedClients.length > 0 && (
                        <p className="mt-2">
                            Everything looks good! Click <b>Import Clients</b> to complete the import so you can start managing them through the Paro platform.
                        </p>
                    )}
                    {!errorInClients && !addClientsManually && !showExistingUsersWarning && importedClients.length > 0 && (
                        <AlertBox
                            title="Connection SUCCESSFUL"
                            message={`${importedClients.length ?? 0} clients successfully connected! Import them now.`}
                            variant="success"
                        />
                    )}
                    {importedClients.length === 0 && (
                        <AlertBox
                            message="We couldn’t find any clients to import. Please make sure your file or QuickBooks account contains client records and try again."
                            variant="info"
                        />
                    )}
                    {errorInClients && importedClients.length > 0 && (
                        <AlertBox
                            title="Some customer details appear to be incomplete or improperly formatted."
                            message="Some client contact details appear to be missing or incorrectly formatted. Please review and provide the necessary information in the correct format."
                            variant="warning"
                        />
                    )}
                    {showExistingUsersWarning && !!importedClientsCount && (
                        <AlertBox
                            title={`${importedClientsCount} clients successfully imported!`}
                            variant="success"
                        />
                    )}
                    {showExistingUsersWarning && (
                        <AlertBox
                            title="Heads up — a few clients are already here"
                            message="We recognized these clients in Paro and skipped them to prevent duplicates. Update their email(s) and submit again."
                            variant="warning"
                        />
                    )}
                    {importedClients.length > 0 && <Box>
                        <div className="flex flex-row justify-between items-center space-x-2 mt-4">
                            <div className="text-xs mt-2"><b>Note: </b> All table cells are editable. Please review and update any client information as needed before proceeding. Ensure accuracy to avoid issues later in the process.</div>
                            <Button
                                label="Add New"
                                onClick={() => { setAddNewClientModal(true) }}
                                size='sm'
                                iconLeft={<IconPlus size="sm" />}
                                color="info"
                            />
                        </div>
                        <div className="flex items-center m-1 mt-2 justify-end items-center">
                            <Checkbox
                                id={'checkbox-select-all'}
                                isChecked={isSelectedAllClients}
                                indeterminate={selectedRows.length > 0 && selectedRows.length < importedClients.length}
                                onChange={handleSelectAllTable}
                                label={isSelectedAllClients ? 'Deselect All' : 'Select All'}
                            />
                        </div>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            id={'checkbox-header'}
                                            indeterminate={(() => {
                                                const start = page * rowsPerPage;
                                                const end = Math.min(start + rowsPerPage, importedClients.length);
                                                const pageIndices = Array.from({ length: end - start }, (_, i) => start + i);
                                                const selectedOnPage = pageIndices.filter(idx => selectedRows.includes(idx)).length;
                                                return selectedOnPage > 0 && selectedOnPage < pageIndices.length;
                                            })()}
                                            isChecked={(() => {
                                                const start = page * rowsPerPage;
                                                const end = Math.min(start + rowsPerPage, importedClients.length);
                                                const pageIndices = Array.from({ length: end - start }, (_, i) => start + i);
                                                return pageIndices.length > 0 && pageIndices.every(idx => selectedRows.includes(idx));
                                            })()}
                                            onChange={handleHeaderCheckbox}
                                        />
                                    </TableCell>
                                    <TableCell><b>S.No</b></TableCell>
                                    <TableCell><b>First Name</b></TableCell>
                                    <TableCell><b>Last Name</b></TableCell>
                                    <TableCell><b>Company Email</b></TableCell>
                                    <TableCell><b>Company Name</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <EditableTableBody
                                importedClients={importedClients}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                setErrorInClients={setErrorInClients}
                                isEditable={true}
                                selectedRows={selectedRows}
                                onRowCheckbox={handleRowCheckbox}
                                setErrorInSelectedClients={setErrorInSelectedClients}
                            />
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        count={importedClients?.length || 0}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        labelRowsPerPage={"Items per page:"}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </Box>}
                </Grid>
                <Box sx={{ marginTop: 10, display: 'flex', flexDirection: 'flex-row', justifyContent: 'flex-end' }}>
                    <Button
                        label="Back"
                        onClick={() => { setCurrentStep(1); setAllClients([]); setImportedClients([]); }}
                        className="mr-2"
                    />
                    {importedClients.length > 0 && <Button
                        label="Import Clients"
                        onClick={() => handleImportClients()}
                        color="primary"
                        isLoading={importingClients}
                        disabled={errorInSelectedClients || !allClients.length || selectedRows.length === 0}
                    />}
                </Box>
            </Grid>
        </Box>
    )
}

export default Step2;